import React, { createContext, useCallback, useContext, useState } from 'react';

import type { TableV2RecordType, TableV2DataObject } from './types';

type TableV2DataContextType<CustomData = object> = {
  rowKeys: string[];
  setRowKeys: React.Dispatch<React.SetStateAction<string[]>>;
  tableData: TableV2DataObject;
  setTableData: React.Dispatch<React.SetStateAction<TableV2DataObject>>;
  getRowByKey: (rowKey: string) => TableV2RecordType<CustomData> | undefined;
  handleUpdateData: (data: TableV2RecordType<CustomData>[]) => void;
  updateCell: (rowKey: string, columnKey: string, value: any) => void;
  updateRow: (rowKey: string, nextRow: TableV2RecordType<CustomData>) => void;
};

const TableV2DataContext = createContext<TableV2DataContextType>({
  rowKeys: [],
  setRowKeys: () => null,
  tableData: {},
  setTableData: () => null,
  getRowByKey: () => undefined,
  handleUpdateData: () => null,
  updateCell: () => null,
  updateRow: () => null,
});

export const TableV2DataProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [rowKeys, setRowKeys] = useState<string[]>([]);
  // NOTE: the tableData must be a FLAT object, regardless if the entries within
  // them have children...since we are accessing data via the rowKey
  const [tableData, setTableData] = useState<TableV2DataObject>({});

  const getRowByKey = useCallback(
    (rowKey: string): TableV2RecordType | undefined => {
      return tableData[rowKey];
    },
    [tableData],
  );

  const handleExternalDataUpdate = useCallback(
    (dataArray: TableV2RecordType[]) => {
      const rowKeys: string[] = [];
      const outObject: TableV2DataObject = {};
      dataArray.forEach((row) => {
        rowKeys.push(row.key);
        outObject[row.key] = row;
      });
      setRowKeys(rowKeys);
      setTableData(outObject);
    },
    [],
  );

  const updateRow = useCallback(
    (rowKey: string, nextRow: TableV2RecordType) => {
      setTableData((prevTableData) => {
        const nextState = {
          ...prevTableData,
          [rowKey]: nextRow,
        };
        return nextState;
      });
    },
    [],
  );

  const updateCell = useCallback(
    (rowKey: string, columnKey: string, value: any) => {
      setTableData((prevTableData) => {
        const updatedRow = { ...(prevTableData[rowKey] || {}) };
        updatedRow[columnKey] = value;
        const nextState = {
          ...prevTableData,
          [rowKey]: updatedRow,
        };
        return nextState;
      });
    },
    [],
  );

  /**
   * TODO: Row manipulation
   * removeRow
   * addRow
   * reorderRows?
   */

  return (
    <TableV2DataContext.Provider
      value={{
        rowKeys,
        setRowKeys,
        tableData,
        setTableData,
        getRowByKey,
        handleUpdateData: handleExternalDataUpdate,
        updateCell,
        updateRow,
      }}
    >
      {children}
    </TableV2DataContext.Provider>
  );
};

export const useTableV2DataContext = <CustomData = object,>() => {
  return useContext<TableV2DataContextType<CustomData>>(TableV2DataContext);
};
